import React, { useEffect, useRef } from "react";
import { Scrollbars } from "react-custom-scrollbars-4";
import { useSnackbar } from "notistack";
import * as XLSX from "xlsx";
import {
  IconButton,
  CircularProgress,
  Backdrop,
  Container,
  Stack,
  Tooltip,
  Toolbar,
  Typography,
  List,
  Collapse,
  Paper,
  Box,
  Breadcrumbs,
  Link,
  Fab,
} from "@mui/material";

import { useNavigate } from "react-router-dom";
import AddCircle from "@mui/icons-material/AddCircle";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import Save from "@mui/icons-material/Save";
// import Delete from "@mui/icons-material/Delete";
// import { checkInvalidField } from "../helpers/checkInvalidField";
import { getSchema } from "../helpers/schemas/getSchema";
// import setupEdit from "../helpers/setupEdit";
import ChecklistRowCard from "./ChecklistRowCard";
import SortDialog from "../Dialogs/SortDialog";
import { getSortArray } from "../helpers/getSortArray";
import { VisuallyHiddenInput } from "../helpers/VisuallyHiddenInput";
import { uploadExcelFile } from "../helpers/uploadExcelFile";
import ImportDialog from "../Dialogs/ImportDialog";
import { getExcelChecklistRows } from "../helpers/getExcelChecklistRows";
import { firestoreAutoId } from "../helpers/firestoreAutoId";
import { saveToFirestore } from "../helpers/saveToFirestore";
import { applyEditTrackingFields } from "../helpers/applyEditTrackingFields";
import { getProject } from "../helpers/getProject";
import { getUserAccess } from "../helpers/getUserAccess";
import AddBoxIcon from "@mui/icons-material/AddBox";
import { getEditorTrackingFieldNames } from "../helpers/schemas/getEditorTrackingFields";

export default function ChecklistTemplateEdit(props) {
  const fileInputRef = useRef(null);
  const fileInputRef1 = useRef(null);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = React.useState(true);
  const [isReady, setIsReady] = React.useState(false);
  const [checklist, setChecklist] = React.useState(null);
  const [rows, setRows] = React.useState([]);
  const [allRows, setAllRows] = React.useState([]);
  // const [editFeature, setEditFeature] = React.useState(null);
  const [editSchema, setEditSchema] = React.useState([]);
  const [importTitle, setImportTitle] = React.useState("New Checklist");
  const [filterValue, setFilterValue] = React.useState("");
  const [sortByValue, setSortByValue] = React.useState("No");
  const [sortByOptions] = React.useState([
    "No",
    "No Desc",
    // "Name",
    // "Name Desc",
    // "Created Date",
    // "Created Date Desc",
  ]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [sheetName, setSheetName] = React.useState(null);
  const [workbook, setWorkBook] = React.useState({ SheetNames: [] });
  const [importDialogOpen, setImportDialogOpen] = React.useState(false);
  const [importSubtitle, setImportSubtitle] = React.useState(
    `Choose the worksheet with the data you wish to import.`,
  );
  const [importData, setImportData] = React.useState([]);
  const [importFields, setImportFields] = React.useState([]);
  const [importStats, setImportStats] = React.useState([]);
  const [isDirty, setIsDirty] = React.useState(false);
  const [project, setProject] = React.useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const params = window.location.href.split("/");
        const projectId = params[params.length - 4];
        const templateId = params[params.length - 2];

        const proj = await getProject(projectId);
        setProject(proj);
        const cklt = proj.ChecklistTemplates.find((c) => c.id === templateId);
        setChecklist(cklt);
        var allress = [...cklt.rows];
        allress.forEach((r) => {
          r.Selected = false;
        });
        onSortChange(sortByValue, allress);
        setAllRows([...allress]);
        setEditSchema(getSchema("checklist-rows"));
      } catch (error) {
        console.error("Error getting Checklist:", error);
        enqueueSnackbar(error.message, {
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "center",
          },
          variant: "error",
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (isLoading === true && isReady === false) {
      setIsReady(true);
      fetchData();
    }
  });

  const onFilterChange = (searchTerm) => {
    if (searchTerm.length > 2) {
      const filteredData = allRows.filter((obj) => {
        const values = Object.values(obj);
        return values.some((value) =>
          String(value).toLowerCase().includes(searchTerm.toLowerCase()),
        );
      });
      setRows([...filteredData]);
    } else {
      setRows([...allRows]);
    }
    setFilterValue(searchTerm);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    // console.log(newPage);
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const onSortChange = (value, array) => {
    if (value !== sortByValue) {
      setSortByValue(value);
    }
    const obs = getSortArray(value, array);
    setRows([...obs]);
  };

  const onSaveRows = async () => {
    try {
      setIsLoading(true);
      var tempRows = [...allRows];
      tempRows.forEach((obj) => {
        delete obj.Selected; // Removes the 'age' property from each object
      });
      checklist.rows = tempRows;
      const item = applyEditTrackingFields(checklist, props.user);
      //Finally save the project
      const updatedProject = { ...project };
      const index = updatedProject.ChecklistTemplates.findIndex(
        (t) => t.id === checklist.id,
      );
      if (index !== -1) {
        updatedProject.ChecklistTemplates[index] = item;
      }
      setProject(updatedProject);
      await saveToFirestore("projects", updatedProject);

      enqueueSnackbar("Checklists rows saved successfully", {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "success",
      });
      setIsLoading(false);
      setIsDirty(false);
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
      setIsLoading(false);
    }
  };

  const onAddRow = () => {
    var item = {};
    var schema = getSchema("checklist-rows");
    schema.forEach((fd) => {
      item[fd.name] = "";
    });

    item["Row Type"] = allRows.length === 0 ? "Header" : "Yes/No/NA";
    item["Row Text"] = "";
    // allRows.length === 0 ? "Header No 1" : "Row text here";
    item["Select Options"] = [];
    item.Selected = false;
    item.No = allRows.length + 1;
    item.id = firestoreAutoId();
    allRows.push(item);
    setAllRows([...allRows]);
    setRows([...allRows]);
    if (isDirty === false) {
      setIsDirty(true);
    }
  };

  const processExcel = async (file) => {
    try {
      if (file) {
        const workbook = await uploadExcelFile(file);
        setWorkBook(workbook);
        setImportDialogOpen(true);
        setImportTitle("Choose a worksheet");
      }
    } catch (error) {
      console.error("Error uploading document:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
    }
  };

  const onCloseImportDialog = () => {
    setImportDialogOpen(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
    if (fileInputRef1.current) {
      fileInputRef1.current.value = null;
    }
    setSheetName(null);
    setImportTitle("Rows Import > Choose Worksheet");
    setImportSubtitle(`Choose the worksheet with the data you wish to import.`);
  };

  const onAttributeChange = (e) => {
    var fd = importFields.find((f) => f.name === e.target.name);
    fd.attribute = e.target.value;
    setImportFields([...importFields]);
  };

  const onFldChange = (e) => {
    var fd = importFields.find((f) => f.name === e.target.name);
    fd.selected = e.target.value;
    setImportFields([...importFields]);
  };

  const onWorksheetSelected = async (e) => {
    try {
      // Assuming you want to read the first sheet
      const worksheet = workbook.Sheets[e];

      // Convert sheet data to an array of objects or an array of arrays
      const jsonData = XLSX.utils.sheet_to_json(worksheet); // Array of objects
      // const arrayOfArrays = XLSX.utils.sheet_to_array(worksheet); // Array of arrays

      console.log(jsonData); // Or update your component state with this data
      if (jsonData.length > 0) {
        var fields = [];
        Object.keys(jsonData[0]).forEach((colName) => {
          var match = editSchema.find((f) => f.name === colName);
          if (match === undefined) {
            editSchema.forEach((fd) => {
              if (
                fd.hasOwnProperty("similair") &&
                fd.similair.includes(colName.toLowerCase())
              )
                match = fd;
            });
          }

          var availableFds = editSchema
            .filter(
              (f) =>
                f.name !== "id" &&
                !f.name.includes("_id") &&
                getEditorTrackingFieldNames().includes(f.name) === false &&
                f.name !== "fileCount" &&
                f.name !== "commentCount" &&
                f.name !== "No",
            )
            .map((f) => f.name);

          availableFds.push("New Attribute");

          fields.push({
            name: colName,
            selected: match ? match.name : "New Attribute",
            options: availableFds,
            attribute: colName,
            error: false,
          });
        });
        setImportData(jsonData);
        setSheetName(e);
        setImportFields(fields);
        setImportTitle("Rows Import > Choose Fields");
        setImportSubtitle(
          `For each column of your spreadsheet, select the corresponding field. The "Row Text" field is required.`,
        );
      } else {
        throw new Error("Unable to import checklist template");
      }
    } catch (err) {
      throw new Error("Unable to import checklist template: " + err.message);
    }
  };

  const onImportExcel = async () => {
    try {
      if (importData.length === 0) {
        onCloseImportDialog();
        return;
      }

      const scores = allRows.map((item) => item.No);
      const maxScore = scores.length > 0 ? Math.max(...scores) : 0;

      const results = await getExcelChecklistRows(
        importData,
        importFields,
        editSchema,
        ["Row Text"],
        maxScore + 1,
      );

      setImportStats([...getStats(results)]);
      setImportSubtitle("Import Successful: Results shown below");
      setImportTitle("Rows Import > Complete");
      setImportFields([]);
      setFilterValue("");
      setPage(0);
      // setProject({ ...results.project });
      setAllRows([...allRows.concat(results)]);
      onSortChange(sortByValue, [...allRows.concat(results)]);
      if (isDirty === false) {
        setIsDirty(true);
      }
      setImportData([]);
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
      // onCloseImportDialog();
    }
  };

  const getStats = (array) => {
    var importStat = [];
    importStat.push({ name: "Rows", count: array.length });
    return importStat;
  };

  const onTextChange = (e) => {
    var row = rows.find((r) => r.id === e.target.name);
    row["Row Text"] = e.target.value;
    setRows([...rows]);

    var roow = allRows.find((r) => r.id === e.target.name);
    roow["Row Text"] = e.target.value;
    setAllRows([...allRows]);

    if (isDirty === false) {
      setIsDirty(true);
    }
  };

  const onRowTypeChange = (e) => {
    var row = rows.find((r) => r.id === e.target.name);
    row["Row Type"] = e.target.value;
    setRows([...rows]);

    var roow = allRows.find((r) => r.id === e.target.name);
    roow["Row Type"] = e.target.value;
    setAllRows([...allRows]);
    if (isDirty === false) {
      setIsDirty(true);
    }

    if (isDirty === false) {
      setIsDirty(true);
    }
  };

  const onRowOptionsChange = (e) => {
    var row = rows.find((r) => r.id === e.id);
    row["Select Options"] = e.newValue;
    setRows([...rows]);

    var roow = allRows.find((r) => r.id === e.id);
    roow["Select Options"] = e.newValue;
    setAllRows([...allRows]);

    if (isDirty === false) {
      setIsDirty(true);
    }
  };

  const handleChecked = (e) => {
    const item = rows.find((item) => item.id === e.target.id);
    item.Selected = !item.Selected;
    setRows([...rows]);
  };

  const onMove = (e) => {
    const itemToMove = allRows.find((x) => x.id === e.id);
    const index = allRows.indexOf(itemToMove); // Find the index of the item

    const [removedItem] = allRows.splice(index, 1); // Remove the item
    allRows.splice(index + (e.direction === "Up" ? -1 : 1), 0, removedItem); // Insert it one position back
    allRows.forEach((r, idx) => {
      r.No = idx + 1;
    });
    onSortChange(sortByValue, allRows);
    setAllRows(allRows);
    if (isDirty === false) {
      setIsDirty(true);
    }
  };

  const onDeleteRow = (id) => {
    const items = allRows.filter((r) => r.id !== id);
    items.sort((a, b) => a.No - b.No);
    items.forEach((r, idx) => {
      r.No = idx + 1;
    });

    const itemss = rows.filter((r) => r.id !== id);
    itemss.sort((a, b) => a.No - b.No);
    itemss.forEach((r, idx) => {
      r.No = idx + 1;
    });

    onSortChange(sortByValue, itemss);
    setAllRows([...items]);

    if (isDirty === false) {
      setIsDirty(true);
    }
  };

  // const onDeleteSelected = () => {
  //   const items = rows.filter((r) => r.Selected === false);
  //   items.sort((a, b) => a.No - b.No);
  //   items.forEach((r, idx) => {
  //     r.No = idx + 1;
  //   });
  //   setRows([...items]);
  //   setAllRows([...items]);
  //   setPage(0);
  //   if (isDirty === false) {
  //     setIsDirty(true);
  //   }
  // };

  return isLoading ? (
    <Backdrop
      sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      open={isLoading}
    >
      <CircularProgress color="inherit" />
    </Backdrop>
  ) : (
    <div style={{ width: "100%", height: "100%" }}>
      <Scrollbars
        autoHide
        autoHideTimeout={10}
        style={{ width: "100%", height: "100%" }}
      >
        <Toolbar />
        <Container
          maxWidth="xl"
          style={{
            // paddingTop: 30,
            paddingBottom: 30,
            // paddingBottom: theme.spacing(4),
            justifyContent: "center",
          }}
        >
          <Typography
            color="primary.light"
            style={{ paddingTop: 40 }}
            variant="h5"
            gutterBottom
          >
            {`Checklist Template: ${checklist !== null && checklist.Name}`}
          </Typography>
          <Stack
            sx={{
              justifyContent: "center",
              alignItems: "center",
            }}
            spacing={2}
          >
            <Breadcrumbs separator="›" aria-label="breadcrumb">
              <Link
                underline="hover"
                key="1"
                color="inherit"
                href=""
                onClick={() =>
                  navigate(`/project/${project.id}/checklist-templates`)
                }
              >
                Checklist Templates
              </Link>
              ,
              <Typography
                underline="hover"
                key="2"
                color="inherit"
                // href={`/project/${checklist.id}/issues`}

                // onClick={handleClick}
              >
                {`Checklist: (${checklist.Name})`}
              </Typography>
              ,
            </Breadcrumbs>
          </Stack>

          <Stack
            // style={{ paddingTop: 10 }}
            direction="row"
            spacing={2}
            sx={{
              justifyContent: "flex-end",
              alignItems: "flex-end",
            }}
          >
            <Tooltip title="Upload Rows">
              <IconButton
                color="primary"
                component="label"
                disabled={
                  !getUserAccess(
                    props.user,
                    project.uid,
                    project.Contacts,
                  ).allowCreate
                }
                role={undefined}
                variant="contained"
                tabIndex={-1}
              >
                <VisuallyHiddenInput
                  type="file"
                  ref={fileInputRef}
                  onChange={(event) =>
                    event.target.files.length > 0 &&
                    processExcel(event.target.files[0])
                  }
                />
                <FileUploadIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Add Row">
              <IconButton
                onClick={onAddRow}
                disabled={
                  !getUserAccess(
                    props.user,
                    project.uid,
                    project.Contacts,
                  ).allowCreate
                }
                aria-label="Add Row"
                size="large"
                color="primary"
              >
                <AddBoxIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Stack>

          <Stack
            style={{ paddingTop: 30 }}
            sx={{
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {allRows.length > 0 && (
              <SortDialog
                sortByValue={sortByValue}
                onSortChange={onSortChange}
                objArray={rows}
                sortByOptions={sortByOptions}
                filterValue={filterValue}
                onFilterChange={onFilterChange}
                page={page}
                handleChangePage={handleChangePage}
                rowsPerPage={rowsPerPage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
              />
            )}
            <Collapse
              style={{ width: "100%" }}
              in={rows.some((item) => item.Selected === true)}
            >
              <Paper elevation={6} style={{ width: "100%" }}>
                <Box
                  sx={{
                    width: "100%",
                    height: 60,
                    borderRadius: 1,
                    bgcolor: "primary.main",
                    // "&:hover": {
                    //   bgcolor: "primary.dark",
                    // },
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                      justifyContent: "flex-start",
                      alignItems: "center",
                    }}
                    style={{ height: "100%", paddingLeft: 16 }}
                  >
                    <Typography color="primary.contrastText">
                      {`Selected ${
                        rows.filter((f) => f.Selected === true).length
                      }`}
                    </Typography>
                    {/* <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<ReportProblem />}
                  >
                    Manage Rows
                  </Button> */}
                    {/* <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<Checklist />}
                  >
                    Manage Checklist
                  </Button> */}
                    {/* <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<EditDocument />}
                  >
                    Manage Files
                  </Button> */}
                    {/* <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<Delete />}
                    onClick={onDeleteSelected}
                  >
                    Delete
                  </Button> */}
                  </Stack>
                </Box>
              </Paper>
            </Collapse>

            {rows.length === 0 ? (
              <Typography>
                There aren't any rows in this checklist. Please
                <Tooltip title="Upload Rows">
                  <IconButton
                    color="primary"
                    component="label"
                    disabled={
                      !getUserAccess(props.user, project.uid, project.Contacts)
                        .allowCreate
                    }
                    role={undefined}
                    variant="contained"
                    tabIndex={-1}
                    size="medium"
                  >
                    <VisuallyHiddenInput
                      type="file"
                      ref={fileInputRef1}
                      onChange={(event) =>
                        event.target.files.length > 0 &&
                        processExcel(event.target.files[0])
                      }
                    />
                    <FileUploadIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
                upload or add
                <Tooltip title="Add Row">
                  <IconButton
                    onClick={onAddRow}
                    aria-label="Add Row"
                    size="medium"
                    color="primary"
                  >
                    <AddCircle fontSize="inherit" />
                  </IconButton>
                </Tooltip>
                a new row.
              </Typography>
            ) : (
              <List style={{ width: "100%" }}>
                <ChecklistRowCard
                  rows={rows}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  // onEdit={onEdit}
                  onMove={onMove}
                  onDeleteRow={onDeleteRow}
                  allowDelete={
                    getUserAccess(props.user, project.uid, project.Contacts)
                      .allowDelete
                  }
                  handleChecked={handleChecked}
                  onTextChange={onTextChange}
                  onRowTypeChange={onRowTypeChange}
                  onRowOptionsChange={onRowOptionsChange}
                />
              </List>
            )}
          </Stack>
          <ImportDialog
            isOpen={importDialogOpen}
            importTitle={importTitle}
            importSubtitle={importSubtitle}
            importData={importData}
            importFields={importFields}
            importStats={importStats}
            sheetName={sheetName}
            workbook={workbook}
            onFldChange={onFldChange}
            onAttributeChange={onAttributeChange}
            onImportExcel={onImportExcel}
            onWorksheetSelected={onWorksheetSelected}
            onClose={onCloseImportDialog}
          />
        </Container>
      </Scrollbars>
      <Stack
        // style={{ paddingTop: 10 }}
        direction="row"
        spacing={2}
        sx={{
          justifyContent: "flex-end",
          alignItems: "flex-end",

          position: "absolute",
          bottom: 16,
          right: 16,
        }}
      >
        <Tooltip title="Save Row Changes">
          <Fab
            color="primary"
            component="label"
            size="medium"
            disabled={
              !isDirty ||
              !getUserAccess(props.user, project.uid, project.Contacts)
                .allowUpdate
            }
            aria-label="Save Rows"
            onClick={onSaveRows}
            role={undefined}
            variant="contained"
          >
            <Save />
          </Fab>
        </Tooltip>
        <Tooltip title="Upload Rows">
          <Fab
            color="primary"
            component="label"
            size="medium"
            disabled={
              !getUserAccess(props.user, project.uid, project.Contacts)
                .allowCreate
            }
            role={undefined}
            variant="contained"
            tabIndex={-1}
          >
            <VisuallyHiddenInput
              type="file"
              ref={fileInputRef}
              onChange={(event) =>
                event.target.files.length > 0 &&
                processExcel(event.target.files[0])
              }
            />
            <FileUploadIcon />
          </Fab>
        </Tooltip>
        <Tooltip title="Add Row">
          <Fab
            onClick={onAddRow}
            disabled={
              !getUserAccess(props.user, project.uid, project.Contacts)
                .allowCreate
            }
            aria-label="Add Row"
            size="medium"
            color="primary"
          >
            <AddCircle />
          </Fab>
        </Tooltip>
      </Stack>
      {/* <Tooltip title="Add Row">
        <Fab
          onClick={onAddRow}
          aria-label="Add Row"
          size="medium"
          color="primary"
          disabled={
            isLoading ||
            !getUserAccess(props.user, project.uid, project.Contacts)
              .allowUpdate
          }
          style={{
            position: "absolute",
            bottom: 16,
            right: 16,
          }}
        >
          <AddCircle />
        </Fab>
      </Tooltip> */}
    </div>
  );
}
